import React, { useState, useEffect, useRef, ReactNode, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import AuthContext, { UserInfo, ViewAsRole } from './AuthContext';
import { oauthConfig } from '../config/oauthConfig';
import { setGlobalRefreshFunction, setGlobalLogoutFunction, setGlobalViewAsRole } from '../../../shared/api/apiClient';
import {
  assignedLevelOf,
  ENTRA_AUTHORIZATION_GROUP,
  LEVEL_GROUPS,
  SUPER_ADMIN_GROUPS,
} from '../../../shared/constants/authorizationGroups';
import { parseCustomGroups, parseGroupsFromClaims } from '../utils/parseGroupsFromClaims';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Cognito JWT payload structure.
 * Note: cognito:groups is an array, custom:groups is a string from SAML mapping.
 */
export interface CognitoJwtPayload {
  sub?: string; // User subject ID (unique identifier)
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  'cognito:groups'?: string[]; // Native Cognito groups (array)
  'custom:groups'?: string; // SAML-mapped groups (string, comma-separated)
  exp?: number;
  iat?: number;
  auth_time?: number;
}

export const parseJwt = (token: string): CognitoJwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as CognitoJwtPayload;
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(hash))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Parse user info from Cognito JWT token.
 * The level comes from the level groups in cognito:groups, the roles from opspilot-admin there and
 * msreportingadmin from either claim, as the backend reads them (ADR-0041). Every other Entra group
 * is shown and grants nothing.
 */
const parseUserFromToken = (payload: CognitoJwtPayload): UserInfo | null => {
  const email = payload.email;
  const name =
    payload.name ||
    `${payload.given_name || ''} ${payload.family_name || ''}`.trim() ||
    email?.split('@')[0];
  const sub = payload.sub;

  if (!email || !sub) {
    console.error('Missing required claims: email or sub');
    return null;
  }

  const cognitoGroups = parseGroupsFromClaims(payload['cognito:groups'], undefined);
  const entraRoles = parseCustomGroups(payload['custom:groups']).filter(g => g === ENTRA_AUTHORIZATION_GROUP);
  const roles = [...new Set([...cognitoGroups, ...entraRoles])].filter(g => SUPER_ADMIN_GROUPS.includes(g));

  return {
    email,
    name,
    sub,
    groups: parseGroupsFromClaims(payload['cognito:groups'], payload['custom:groups']),
    assignedLevel: assignedLevelOf(cognitoGroups),
    roles,
  };
};

// The previewable levels and the one level group each carries; the roles are dropped (backend view_as.py).
const VIEW_AS_LEVEL_GROUP: Record<ViewAsRole, string> = Object.fromEntries(
  Object.entries(LEVEL_GROUPS).map(([group, level]) => [level, group]),
) as Record<ViewAsRole, string>;

const SESSION_KEY = 'opspilot_view_as_role';

/** The stored view-as level, or null: a former country role the backend would refuse is dropped. */
const storedViewAsRole = (): ViewAsRole | null => {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored && stored in VIEW_AS_LEVEL_GROUP) return stored as ViewAsRole;
  sessionStorage.removeItem(SESSION_KEY);
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole | null>(storedViewAsRole);

  const setViewAsRole = useCallback((role: ViewAsRole | null) => {
    setViewAsRoleState(role);
    if (role) {
      sessionStorage.setItem(SESSION_KEY, role);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
    setGlobalViewAsRole(role);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIdToken(null);
    setIsAuthenticated(false);
    setViewAsRoleState(null);
    sessionStorage.clear();

    const params = new URLSearchParams({
      client_id: oauthConfig.clientId,
      logout_uri: oauthConfig.logoutUri,
    });

    window.location.href = `${oauthConfig.logoutEndpoint}?${params}`;
  }, []);

  const logoutRef = useRef(logout);
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  const refreshCredentials = useCallback(async () => {
    const storedRefreshToken = sessionStorage.getItem('refresh_token');

    if (!storedRefreshToken) {
      console.log('No refresh token available, logging out...');
      logoutRef.current();
      return;
    }

    try {
      console.log('Refreshing ID token...');
      const tokenResponse = await fetch(oauthConfig.tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: oauthConfig.clientId,
          grant_type: 'refresh_token',
          refresh_token: storedRefreshToken,
        }),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`Token refresh failed: ${tokenResponse.statusText} - ${errorText}`);
      }

      const tokens = await tokenResponse.json();
      const newIdToken = tokens.id_token;
      const newRefreshToken = tokens.refresh_token;

      if (!newIdToken) {
        throw new Error('No ID token received from refresh');
      }

      sessionStorage.setItem('cognito_id_token', newIdToken);
      if (newRefreshToken) {
        sessionStorage.setItem('refresh_token', newRefreshToken);
      }

      const payload = parseJwt(newIdToken);
      if (payload) {
        const userInfo = parseUserFromToken(payload);
        if (userInfo) {
          setUser(userInfo);
          sessionStorage.setItem('user_info', JSON.stringify(userInfo));
        }
      }

      setIdToken(newIdToken);
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
      logoutRef.current();
    }
  }, []);

  /**
   * Initiate login via the O365fi Entra identity provider (ADR-0051).
   */
  const login = useCallback(async () => {
    const state = crypto.randomUUID();
    sessionStorage.setItem('oauth_state', state);

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: oauthConfig.clientId,
      response_type: 'code',
      redirect_uri: oauthConfig.redirectUri,
      scope: oauthConfig.scopes.join(' '),
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      identity_provider: 'O365fi',
    });

    window.location.href = `${oauthConfig.authEndpoint}?${params}`;
  }, []);

  const handleCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      console.error('OAuth error:', error, params.get('error_description'));
      setIsInitializing(false);
      return;
    }

    if (!code) {
      // No code in URL - try to restore session from storage
      const storedToken = sessionStorage.getItem('cognito_id_token');
      const storedUser = sessionStorage.getItem('user_info');

      if (storedToken && storedUser) {
        try {
          const tokenData = parseJwt(storedToken);
          // Derived from the token, not the stored copy, which may predate the current fields.
          const user = tokenData ? parseUserFromToken(tokenData) : null;

          if (!tokenData || !tokenData.exp || !user) {
            console.log('Invalid token data, clearing session');
            sessionStorage.clear();
            setIsInitializing(false);
            return;
          }

          const expirationDate = new Date(tokenData.exp * 1000);
          const now = new Date();

          if (expirationDate > now) {
            sessionStorage.setItem('user_info', JSON.stringify(user));
            setIdToken(storedToken);
            setUser(user);
            setIsAuthenticated(true);
          } else {
            console.log('JWT token expired, clearing session');
            sessionStorage.clear();
          }
        } catch (error) {
          console.error('Failed to restore session:', error);
          sessionStorage.clear();
        }
      }
      setIsInitializing(false);
      return;
    }

    // Validate state parameter (CSRF protection)
    const savedState = sessionStorage.getItem('oauth_state');
    if (state !== savedState) {
      console.error('State mismatch - possible CSRF attack');
      sessionStorage.clear();
      setIsInitializing(false);
      return;
    }

    try {
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
      if (!codeVerifier) {
        throw new Error('PKCE code verifier not found');
      }

      // Exchange authorization code for tokens
      const tokenResponse = await fetch(oauthConfig.tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: oauthConfig.clientId,
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: oauthConfig.redirectUri,
          code_verifier: codeVerifier,
        }),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`Token request failed: ${tokenResponse.statusText} - ${errorText}`);
      }

      const tokens = await tokenResponse.json();
      const newIdToken = tokens.id_token;
      const refreshToken = tokens.refresh_token;

      if (!newIdToken) {
        throw new Error('No ID token received from Cognito');
      }

      const payload = parseJwt(newIdToken);
      if (!payload) {
        throw new Error('Failed to parse ID token');
      }

      const userInfo = parseUserFromToken(payload);
      if (!userInfo) {
        throw new Error('Failed to extract user info from token');
      }

      // Store tokens and user info
      sessionStorage.setItem('cognito_id_token', newIdToken);
      sessionStorage.setItem('user_info', JSON.stringify(userInfo));
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken);
      }

      setIdToken(newIdToken);
      setUser(userInfo);
      setIsAuthenticated(true);

      // Return to the originally requested deep link, if any, otherwise clean
      // the OAuth params (?code=&state=) off the callback URL.
      const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
      sessionStorage.removeItem('post_login_redirect');
      if (postLoginRedirect && postLoginRedirect !== '/login') {
        // Hard redirect to the originally requested deep link. An SPA navigate()
        // here races with the /auth/callback -> "/" -> resume-last-customer
        // chain (useCustomerSelection), which drops the query string. A full
        // reload lands directly on the target so nothing can strip the param.
        window.location.replace(postLoginRedirect);
        return;
      }
      // Clean the OAuth params (?code=&state=) off the callback URL.
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Authentication failed:', error);
      sessionStorage.clear();
    } finally {
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => { handleCallback(); }, [handleCallback]);

  useEffect(() => {
    if (!isAuthenticated || !idToken) {
      return;
    }

    const tokenData = parseJwt(idToken);
    if (!tokenData || !tokenData.exp) {
      return;
    }

    const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;

    // Refresh 5 minutes before expiration
    const refreshTime = timeUntilExpiry - 5 * 60 * 1000;

    if (refreshTime <= 0) {
      // Token already expired or about to expire, refresh immediately
      console.log('Token expired or expiring soon, refreshing now');
      refreshCredentials();
      return;
    }

    console.log(`Token will be refreshed in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    const timerId = setTimeout(() => {
      refreshCredentials();
    }, refreshTime);

    return () => clearTimeout(timerId);
  }, [isAuthenticated, idToken, refreshCredentials]);

  useEffect(() => {
    setGlobalRefreshFunction(refreshCredentials);
    setGlobalLogoutFunction(logout);
    return () => {
      setGlobalRefreshFunction(null!);
      setGlobalLogoutFunction(null!);
    };
  }, [refreshCredentials, logout]);

  // Sync view-as role with apiClient
  useEffect(() => {
    setGlobalViewAsRole(viewAsRole);
  }, [viewAsRole]);

  const isRealAdmin = useMemo(() => {
    return (user?.roles.length ?? 0) > 0;
  }, [user]);

  // Compute effective user when view-as is active
  const effectiveUser = useMemo(() => {
    if (!user || !viewAsRole || !isRealAdmin) return user;
    return { ...user, groups: [VIEW_AS_LEVEL_GROUP[viewAsRole]], assignedLevel: viewAsRole, roles: [] };
  }, [user, viewAsRole, isRealAdmin]);

  const isAdmin = useMemo(() => {
    if (viewAsRole && isRealAdmin) return false;
    return isRealAdmin;
  }, [viewAsRole, isRealAdmin]);

  const hasAdminAccess = useCallback(() => isAdmin, [isAdmin]);

  const contextValue = useMemo(() => ({
    isAuthenticated,
    user: effectiveUser,
    idToken,
    isAdmin,
    isRealAdmin,
    viewAsRole,
    setViewAsRole,
    login,
    logout,
    refreshCredentials,
    hasAdminAccess,
  }), [isAuthenticated, effectiveUser, idToken, isAdmin, isRealAdmin, viewAsRole, setViewAsRole, login, logout, refreshCredentials, hasAdminAccess]);

  if (isInitializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div>Authenticating...</div>
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
